import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { isEqual, omit } from 'lodash';
import classnames from 'classnames';

import ContextMenu from './ContextMenu';
import { ContextMenuItem as ContextMenuItemType } from 'types';

type HOCProps = {
  containerClassName?: string,
  position: 'bottom' | 'top',
  forceEventPropagation?: boolean,
  canEmpty?: boolean
}

type ContextMenuHOCProps = {
  contextMenuProps: {
    items: Array<ContextMenuItemType>
  } & { [x: string]: any }
};


export function withContextMenu(
  WrappedComponent,
  hocProps: HOCProps
) {
  class ContextMenuHOC extends React.Component<React.ComponentProps<typeof WrappedComponent> & ContextMenuHOCProps> {
    static displayName = `ContextMenuHOC(${WrappedComponent.displayName || WrappedComponent.name || 'Component'}`;
    target: HTMLElement | null = null;

    menuContainer = document.createElement('div');

    state = {
      contextMenu: null
    };

    componentWillUnmount() {
      if (document.body && document.body.contains(this.menuContainer)) {
        document.body.removeChild(this.menuContainer);
      }
    }

    shouldComponentUpdate(nextProps, nextState) {
      const { contextMenuProps } = this.props;
      const { contextMenu } = this.state;

      return !isEqual(contextMenuProps.items, nextProps.contextMenuProps.items) || (contextMenu !== nextState.contextMenu);
    }

    handleShow = (event) => {
      const { position, forceEventPropagation } = hocProps;
      const { contextMenuProps } = this.props;

      if (!forceEventPropagation) {
        event.stopPropagation();
      }

      this.setState({
        contextMenu: ReactDOM.createPortal(
          <ContextMenu
            key="context"
            {...contextMenuProps}
            position={position}
            target={this.target!}
            onClose={this.handleClose}
          />,
          this.menuContainer
        )
      });

      if (document.body) {
        document.body.appendChild(this.menuContainer);
      }
    };

    handleClose = (event) => {
      if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }

      this.setState({
        contextMenu: null
      });
    };

    render() {
      const { contextMenu } = this.state;
      const { containerClassName = 'context-menu__wrapper', canEmpty } = hocProps;
      const { contextMenuProps } = this.props;

      if (contextMenuProps.items.length === 0 && !canEmpty) {
        return null;
      }

      const wrappedProps = omit(this.props, 'contextMenuProps');

      const wrapperClasses = classnames('context-menu__wrapper', {
        [containerClassName]: containerClassName,
        [`${containerClassName}--active`]: containerClassName && contextMenu
      });

      return [
        <div
          key="context-menu__wrapper"
          className={wrapperClasses}
          ref={(ref) => {
            this.target = ref;
          }} // I could not set ref to WrappedComponent => https://reactjs.org/docs/higher-order-components.html
        >
          <WrappedComponent
            {...wrappedProps}
            onClick={this.handleShow}
          />
        </div>,
        contextMenu
      ];
    }
  }

  return ContextMenuHOC;
}
